import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';

interface TrustedDevice {
  id: string;
  deviceName: string;
  location: string;
  token: string;
  status: 'TOKEN_GENERATED' | 'REGISTERED' | 'APPROVED' | 'REJECTED' | 'BLOCKED';
  fingerprint?: string;
  ipAddress?: string;
  macAddress?: string;
  serialNumber?: string;
  userAgent?: string;
  screenResolution?: string;
  timezone?: string;
  lastSeen?: string;
  notes?: string;
  allowedIpRanges: string[];
  workingHours?: string;
  allowedDays: string[];
  approvedBy?: string;
  approvedAt?: string;
  registeredAt?: string;
  createdAt: string;
}

interface DeviceDialogProps {
  device: TrustedDevice | null;
  open: boolean;
  onClose: () => void;
  onSave: (id: string | null, data: any) => Promise<void>;
}

export const DeviceDialog: React.FC<DeviceDialogProps> = ({
  device,
  open,
  onClose,
  onSave,
}) => {
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    deviceName: '',
    location: '',
    notes: '',
    allowedIpRanges: '',
    workingHours: '',
    allowedDays: ''
  });

  useEffect(() => {
    if (device) {
      // Edit mode
      setFormData({
        deviceName: device.deviceName,
        location: device.location,
        notes: device.notes || '',
        allowedIpRanges: device.allowedIpRanges.join(', '),
        workingHours: device.workingHours || '',
        allowedDays: device.allowedDays.join(', ')
      });
    } else {
      // Create mode - reset form
      setFormData({
        deviceName: '',
        location: '',
        notes: '',
        allowedIpRanges: '',
        workingHours: '',
        allowedDays: ''
      });
    }
    setFormError('');
  }, [device, open]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!formData.deviceName.trim() || !formData.location.trim()) {
      setFormError('Nazwa urządzenia i lokalizacja są wymagane');
      return;
    }

    try {
      setSaving(true);

      const saveData: any = {
        deviceName: formData.deviceName.trim(),
        location: formData.location.trim(),
        notes: formData.notes.trim() || null,
      };

      // Add optional fields only when editing
      if (device) {
        saveData.allowedIpRanges = formData.allowedIpRanges
          ? formData.allowedIpRanges.split(',').map(ip => ip.trim())
          : [];
        saveData.workingHours = formData.workingHours.trim() || null;
        saveData.allowedDays = formData.allowedDays
          ? formData.allowedDays.split(',').map(day => day.trim())
          : [];
      }

      await onSave(device?.id || null, saveData);

      // Success toast
      toast.success(device
        ? `Urządzenie "${formData.deviceName}" zostało zaktualizowane`
        : `Urządzenie "${formData.deviceName}" zostało dodane i token wygenerowany`
      );

      onClose();
    } catch (error: any) {
      const errorMessage = error.message || 'Wystąpił błąd podczas zapisywania urządzenia';
      setFormError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="sticky top-0 z-10 bg-background px-6 pt-6 pb-4 border-b">
          <DialogTitle>
            {device ? `Edytuj urządzenie: ${device.deviceName}` : 'Dodaj nowe urządzenie'}
          </DialogTitle>
          <DialogDescription>
            {device
              ? 'Zaktualizuj ustawienia urządzenia'
              : 'Dodaj urządzenie i wygeneruj token do rejestracji'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto px-6 py-4">
          <form id="device-form" onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <div className="text-red-500 text-sm bg-red-50 p-3 rounded-md border border-red-200">
                {formError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="deviceName">Nazwa urządzenia <span className="text-red-500">*</span></Label>
                <Input
                  id="deviceName"
                  value={formData.deviceName}
                  onChange={(e) => handleInputChange('deviceName', e.target.value)}
                  placeholder="np. Tablet - Wejście główne"
                  required
                />
              </div>
              <div>
                <Label htmlFor="location">Lokalizacja <span className="text-red-500">*</span></Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="np. Hala produkcyjna A"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Notatki</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Dodatkowe informacje o urządzeniu..."
                rows={3}
              />
            </div>

            {device && (
              <Tabs defaultValue="security" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="security">Bezpieczeństwo</TabsTrigger>
                  <TabsTrigger value="restrictions">Ograniczenia</TabsTrigger>
                </TabsList>

                <TabsContent value="security" className="space-y-4">
                  <div>
                    <Label htmlFor="allowedIpRanges">Dozwolone zakresy IP</Label>
                    <Input
                      id="allowedIpRanges"
                      value={formData.allowedIpRanges}
                      onChange={(e) => handleInputChange('allowedIpRanges', e.target.value)}
                      placeholder="np. 192.168.1.0/24, 10.0.0.0/8"
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      Oddziel wiele zakresów przecinkami (opcjonalne)
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="restrictions" className="space-y-4">
                  <div>
                    <Label htmlFor="workingHours">Godziny pracy</Label>
                    <Input
                      id="workingHours"
                      value={formData.workingHours}
                      onChange={(e) => handleInputChange('workingHours', e.target.value)}
                      placeholder="np. 06:00-22:00"
                    />
                  </div>

                  <div>
                    <Label htmlFor="allowedDays">Dozwolone dni</Label>
                    <Input
                      id="allowedDays"
                      value={formData.allowedDays}
                      onChange={(e) => handleInputChange('allowedDays', e.target.value)}
                      placeholder="np. mon, tue, wed, thu, fri"
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      Dostępne: mon, tue, wed, thu, fri, sat, sun
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </form>
        </div>

        <DialogFooter className="sticky bottom-0 z-10 bg-background px-6 pb-6 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Anuluj
          </Button>
          <Button type="submit" form="device-form" disabled={saving}>
            {saving ? 'Zapisywanie...' : (device ? 'Zapisz zmiany' : 'Utwórz i wygeneruj token')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
